import baseProductFieldMapFactory from '../mappers/baseProductFieldMapFactory'
import applyMap from './applyMap'
import normalizeRoleAdjustPrices from './normalizeRoleAdjustPrices'

// Detail-page-only fields (full-text content, reviews, related products) -
// confirmed via a full-codebase usage audit to be read only by
// single-product-page views and the CMS editor, never by any catalog
// listing/card/carousel. Stripped from the SSR-inlined initial state to
// avoid shipping them on every page load; the product/wine-club selection
// views lazy-fetch them post-hydration via products/loadProductByQuery
// (which already requests all of these - see PRODUCT_ID_QUERY) when
// they're missing. Client-side (non-SSR) rendering never strips these, so
// a fresh client-side catalog load still gets full data.
//
// imagesGallery deliberately NOT included here despite matching the same
// audit - it's used as the primary above-the-fold product image on the
// detail page (item.imagesGallery[0]), not secondary/below-the-fold
// content, so deferring it would blank out the main product photo (and
// hurt SEO/crawlers that don't run JS) until the lazy-fetch completes.
//
// dateChanged and sticky deliberately NOT included despite otherwise
// matching this audit - both are used as sort keys/comparators on the
// catalog listing itself (dateChanged: the "sort by newest" dropdown in
// content-creator's Type.vue/wine-catalogue-page.vue; sticky: the fallback
// sort in winecatalog-preload-helper.js when no custom sort-order blob is
// set) - deferring either would silently break catalog ordering.
const SSR_DEFERRED_PRODUCT_FIELDS = [
  'wine_making',
  'reviews',
  'technical_notes',
  'tasting_notes',
  'prev_tasting_notes',
  'related_products',
  'faq_categories',
  'substitute_products',
  'product_links',
  'product_links_name',
  'unit_of_sale',
  'changed',
  'modified',
  'created',
  'awards',
  'sweetness',
  'sweetnessDesc',
  'wishlist',
  'max_cart',
  'max_club',
  'min_club'
]

// timings is an optional {clone, baseMap, typeMap} accumulator a caller
// processing many products (e.g. detectAndMapProducts) can pass in to
// measure where time goes across the whole batch - temporary diagnostic
// instrumentation, not the app's profiler (531 individual profiler
// entries would be too noisy to be useful here; this sums into one
// caller-owned object instead).
export default function applyFieldMapper(requestedFieldMap = {}, store, product, baseProductMap = null, timings = null, forceLite = false) {
  const t0 = timings ? Date.now() : 0
  const alteredProduct = JSON.parse(JSON.stringify(product))
  if (timings) timings.clone += Date.now() - t0

  // Generate the base product mappings object, unless a caller processing
  // many products against the same taxonomy/variable maps (e.g. a full
  // catalog listing) already computed it once and passed it in - this
  // factory call was showing up as redundant per-product cost when called
  // in a loop, since taxonomy/variable don't change between products in
  // the same request.
  if (baseProductMap === null) {
    baseProductMap = baseProductFieldMapFactory(
      store.getters['products/getMap']('taxonomy'),
      store.getters['products/getMap']('variable')
    )
  }

  // Apply the base mappings that get applied to all products.
  const t1 = timings ? Date.now() : 0
  let baseMappedProduct = applyMap(baseProductMap, alteredProduct, store)
  if (timings) timings.baseMap += Date.now() - t1

  if (requestedFieldMap !== '') {
    // Apply the specific requested mapping to the product and return the result.
    const t2 = timings ? Date.now() : 0
    const additionalMap = applyMap(requestedFieldMap, baseMappedProduct, store)
    if (timings) timings.typeMap += Date.now() - t2

    return normalizeRoleAdjustPrices(store, stripSsrDeferredFields(additionalMap, forceLite))
  }
  else {
    return normalizeRoleAdjustPrices(store, stripSsrDeferredFields(baseMappedProduct, forceLite))
  }
}

// forceLite lets a caller request the same trimming outside of an actual
// SSR build - e.g. the client-side "lite catalog" fetch that runs
// immediately on hydration to backfill everything pruneUnreferencedProducts
// removed from the SSR-inlined state (see store/index.js). Client-side
// rendering that doesn't explicitly ask for lite still gets full data, as
// before.
function stripSsrDeferredFields(product, forceLite = false) {
  if (process.env.SERVER_BUILD === 'ssr' || forceLite) {
    SSR_DEFERRED_PRODUCT_FIELDS.forEach(field => { delete product[field] })
  }
  return product
}
